import type { PatientData } from './types'
import type { ZodError } from 'zod'

import _ from 'lodash'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useParams } from 'react-router'
import { useNavigate } from 'react-router-dom'

import draft from '../../lib/draft'
import http from '../../lib/http'
import { useAppSelector } from '../../store'
import { LoadingIndicator } from '../claim-history/simple-table/loading-indicator'
import { useGlobalLoadingIndicator } from '../global-loading-indicator'
import { Warning } from '../igao/components/warnings/warning'
import { PlainButton } from '../shared/Button'
import * as Card from '../shared/card'

import { ErrorHandler } from './components/error-handler'
import { CreatePatientForm } from './components/registrate-form'
import { newPatientSchema, useUbaCreateMutation } from './queries'

const CREATE_UBA_USER_LOADER_ID = 'create-uba-user'

export type PatientState = PatientData & { draft: boolean }

export function CreateUserPage (): JSX.Element {
  let createMutation = useUbaCreateMutation()
  const navigate = useNavigate()
  const globalLoading = useGlobalLoadingIndicator(CREATE_UBA_USER_LOADER_ID)
  const params = useParams<{ id: string, clientName: string }>()
  const [ state, setState ] = useState<Partial<PatientState>>()
  const [ initialState, setInitialState ] = useState<Partial<PatientState>>()
  const [ isValid, setIsValid ] = useState(false)
  const [ latestError, setLatestError ] = useState(null)
  const [ formError, setFormError ] = useState<ZodError | null>(null)

  let user = useAppSelector((state) => state.users.current_user)

  async function fetchUser (id: string): Promise<PatientData> {
    let response = await http.getRequest(`/uba/${id}/info`, {})
    setState(response)
    setInitialState(response)
    const { success, error } = newPatientSchema.safeParse(response.data)
    setIsValid(success)
    setFormError(error ?? null)
    return response
  }

  useEffect(() => {
    fetchUser(params.id ?? '')
      .catch(() => {
        toast.error("Something went wrong while fetching user's data")
      })
  }, [ params.id ])

  useEffect(() => {
    if (state && !_.isEqual(state.data, initialState?.data)) {
      setInitialState(state)
      draft.set({
        resource: params.id,
        path: 'uba-patient-form',
        user: user?.id,
        data: state.data
      }).then(() => {
        setState((prev) => ({ ...prev, draft: true }))
        const { success, error } = newPatientSchema.safeParse(state.data)
        setIsValid(success)
        setFormError(error ?? null)
      })?.catch((e) => console.error(e))
    }
  }, [ initialState?.data, params.id, state, user?.id ])

  if (!state) {
    return (
      <LoadingIndicator loading />
    )
  }

  return (
    <Card.Card expandable>
      <Card.Header>
        <div className='w-full'>
          <h2>Uba Care User</h2>
        </div>
      </Card.Header>
      <Card.ExpandableWrapper>
        <CreatePatientForm
          formError={formError}
          state={state}
          setState={setState}
        />
      </Card.ExpandableWrapper>

      <div className='px-4'>
        {createMutation.isError && <ErrorHandler error={latestError} />}
      </div>

      {!state.ubaUser && (
        <>
          <hr className='my-[28px]' />

          {(!state?.data?.patientId || !state.data.patientId.length) && (
            <div className='mx-8 my-4'>
              <Warning
                title='Member ID is missing in Compassion'
                subTitle='UBACARE orders cannot be placed until this information is provided. Please reach out to your administrator for support.'
                data={[]}
                type='conflict'
                withMargin={false}
              />
            </div>
          )}

          <div
            style={{
              textAlign: 'right',
              padding: '0 28px 28px 0'
            }}
          >

            {/* <DraftButton /> */}
            <PlainButton
              style={{ marginLeft: '6px' }}
              disabled={!state.draft}
              label='Discard changes'
              btnType='contained'
              onClick={() => {
                draft.remove({
                  resource: params.id,
                  path: 'uba-patient-form',
                  user: user?.id
                }).then(async () => await fetchUser(params.id ?? '')).catch((e) => console.error(e))
              }}
            />
            <PlainButton
              style={{ marginLeft: '12px' }}
              disabled={!state.draft || !isValid || createMutation.isPending}
              label='Publish'
              color='primary'
              btnType='contained'
              onClick={() => {
                globalLoading.startLoading('Creating Uba User')
                createMutation.mutate({ id: params.id ?? '' }, {
                  async onSuccess () {
                    navigate(`/patient/${params?.clientName ?? ''}/${params?.id ?? ''}/assessment/uba`)
                  },
                  async onError (e: any) {
                    setLatestError(e?.response?.data ?? { message: e?.message })
                  },
                  async onSettled () {
                    globalLoading.finishLoading()
                  }
                })
              }}
            />
          </div>
        </>
      )}
    </Card.Card>
  )
}
